import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook that handles the OAuth callback flow:
 * Extracts session_id from URL hash → exchanges via backend → navigates to dashboard
 */
export function useAuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  const exchangeSession = useCallback(async (sessionId) => {
    const response = await fetch(`${API_URL}/api/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId })
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Session exchange failed');
    }

    const data = await response.json();
    if (!data.success || !data.user) {
      throw new Error('No user data returned');
    }
    return data.user;
  }, []);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          logger.error('No session_id found in URL hash');
          navigate('/', { replace: true });
          return;
        }

        const user = await exchangeSession(sessionId);
        navigate('/dashboard', { replace: true, state: { user } });
      } catch (error) {
        logger.error('OAuth callback error:', error);
        navigate('/', { replace: true });
      }
    };

    processCallback();
  }, [navigate, exchangeSession]);
}
